import type {SvgIconComponent} from "@mui/icons-material";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import KeyboardVoiceOutlinedIcon from "@mui/icons-material/KeyboardVoiceOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import {Box, Stack} from "@mui/material";
import type {App} from "crqlar-react-common/dist/models/app/app.model";
import {AppCategory} from "crqlar-react-common/dist/models/app/app.model";
import type {OrganizationApp} from "crqlar-react-common/dist/models/app/organizationApp.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useTranslation} from "react-i18next";
import {useEffect, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {getSectorIcon} from "crqlar-react-common/dist/components/sectorIcons/sectorIcons";
import SurveyModuleIcon from "../../common/icons/SurveyModuleIcon";
import {useMe} from "../../common/useMe";
import {useCrossSaleOpportunities} from "../../crossSale/useCrossSaleOpportunities";
import {buildAppLink} from "../appNavigation";
import {SidebarNavButton} from "./SidebarNavButton";
import SidebarHeader from "./SidebarHeader";
import {SIDEBAR_CONTENT_X_PADDING} from "./sidebarLayout";
import UserMenu, {type UserMenuPointerPosition} from "./UserMenu";

interface SidebarIconDefinition {
    sku: string;
    specialization?: string;
    Icon: SvgIconComponent;
}

const SIDEBAR_APP_ICONS = [
    {sku: "crm", Icon: PeopleAltOutlinedIcon},
    {sku: "reservations", specialization: "restaurant", Icon: RestaurantMenuOutlinedIcon},
    {sku: "reservations", specialization: "spa", Icon: SpaOutlinedIcon},
    {sku: "reservations", specialization: "accommodation", Icon: HotelOutlinedIcon},
    {sku: "reservations", specialization: "weekly-planner", Icon: EditCalendarOutlinedIcon},
    {sku: "reservations", Icon: EventOutlinedIcon},
    {sku: "correspondence", Icon: ChatOutlinedIcon},
    {sku: "booking", Icon: WidgetsOutlinedIcon},
    {sku: "marketing", Icon: CampaignOutlinedIcon},
    {sku: "surveys", Icon: SurveyModuleIcon},
    {sku: "voiceNote", Icon: KeyboardVoiceOutlinedIcon},
] satisfies SidebarIconDefinition[];

const FOOTER_LINKS = [
    {
        id: "knowledge-base",
        label: "Knowledge Base",
        Icon: SchoolOutlinedIcon,
        link: "https://crqlar.notion.site/Knowledge-Base-new-35f9e2a69b99804b8c9fd83f4f795e87",
        external: true,
    },
    {
        id: "settings",
        label: "Settings",
        Icon: SettingsOutlinedIcon,
        link: "/system",
    },
];

const normalizePath = (path: string) => (
    path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path
);

const isActivePath = (pathname: string, link: string) => {
    const currentPath = normalizePath(pathname);
    const targetPath = normalizePath(link);

    return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
};

const isAppActive = (pathname: string, app: App, orgApp: OrganizationApp) => {
    if (!isActivePath(pathname, app.link)) return false;
    if (app.sku === "booking") return true;
    if (app.sku === "marketing" && isActivePath(pathname, "/marketing/insights")) return true;
    if (!orgApp.id) return false;

    return pathname.split("/").includes(orgApp.id);
};

export const findSidebarAppIcon = (app: Pick<App, "sku" | "specialization">) => SIDEBAR_APP_ICONS.find(definition => (
    definition.sku === app.sku
    && (!definition.specialization || definition.specialization === app.specialization)
));

interface SidebarContentProps {
    expanded: boolean;
    onUserMenuOpenChange?: (open: boolean, pointerPosition?: UserMenuPointerPosition) => void;
}

const SidebarContent = ({expanded, onUserMenuOpenChange}: SidebarContentProps) => {
    const {pathname} = useLocation();
    const {t, i18n} = useTranslation();
    const {
        availableApps,
    } = useMe();
    const allSectors = useAllSectors();
    const {lockedApps} = useCrossSaleOpportunities();
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const scrollContentRef = useRef<HTMLDivElement | null>(null);
    const [hasScrollableContent, setHasScrollableContent] = useState(false);

    const appItems = availableApps.flatMap(({app, orgApp}) => {
        if (!app || app.category !== AppCategory.APP) return [];

        const active = isAppActive(pathname, app, orgApp);
        const defaultIcon = findSidebarAppIcon(app);
        const sector = allSectors.find(item => item.id === orgApp.configuration?.sectorId);
        const Icon = getSectorIcon(sector) ?? defaultIcon?.Icon ?? AppsOutlinedIcon;

        return [{
            active,
            Icon,
            id: orgApp.id || `${app.sku}-${app.id}`,
            /*
                The customer's own name for their installation wins; otherwise the marketplace app's
                name in the user's language, falling back to English and then to the plain `name`.
            */
            label: orgApp.name
                || app.nameLocalized?.[i18n.language]
                || app.nameLocalized?.en
                || app.name
                || app.sku,
            link: buildAppLink(app, orgApp),
        }];
    });

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        const scrollContent = scrollContentRef.current;
        if (!scrollContainer || !scrollContent) return undefined;

        const updateOverflow = () => {
            setHasScrollableContent(scrollContainer.scrollHeight - scrollContainer.clientHeight > 1);
        };
        const resizeObserver = new ResizeObserver(updateOverflow);

        resizeObserver.observe(scrollContainer);
        resizeObserver.observe(scrollContent);
        updateOverflow();

        return () => resizeObserver.disconnect();
    }, [appItems.length, lockedApps.length]);

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                bgcolor: "background.paper",
                color: "primary.dark",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}
        >
            <Box
                ref={scrollContainerRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    // Thumb only on hover. Standard props, not ::-webkit-scrollbar: Chrome doesn't repaint that on hover.
                    scrollbarWidth: "thin",
                    scrollbarColor: "transparent transparent",
                    "&:hover": {
                        scrollbarColor: "rgba(15, 23, 42, 0.2) transparent",
                    },
                }}
            >
                <Stack ref={scrollContentRef} sx={{minHeight: "100%"}}>
                    <SidebarHeader expanded={expanded}/>

                    {/* Collapsed: no right padding, rows stop at the icon column, so the scroll bar never squeezes them */}
                    <Stack
                        gap={0.75}
                        sx={{
                            pl: `${SIDEBAR_CONTENT_X_PADDING}px`,
                            pr: expanded ? `${SIDEBAR_CONTENT_X_PADDING}px` : 0,
                            pt: 0.75,
                        }}
                    >
                        {appItems.map(item => (
                            <SidebarNavButton
                                key={item.id}
                                active={item.active}
                                expanded={expanded}
                                Icon={item.Icon}
                                id={`dashboard-sidebar-app-${item.id}`}
                                label={item.label}
                                link={item.link}
                            />
                        ))}

                        {/* Cross-selling: apps the organization is not subscribed to open a blurred preview */}
                        {lockedApps.map(locked => {
                            const Icon = findSidebarAppIcon(locked.opportunity)?.Icon ?? AppsOutlinedIcon;
                            const active = isActivePath(pathname, locked.link);

                            return (
                                <SidebarNavButton
                                    key={locked.opportunity.key}
                                    active={active}
                                    expanded={expanded}
                                    id={`dashboard-sidebar-preview-${locked.opportunity.slug}`}
                                    label={locked.name}
                                    muted
                                    ariaLabel={`${locked.name} · ${t("Preview")}`}
                                    tooltipTitle={`${locked.name} · ${t("Preview")}`}
                                    link={locked.link}
                                    leading={(
                                        <Box sx={{position: "relative", display: "flex", opacity: active ? 1 : 0.6}}>
                                            <Icon sx={{fontSize: 22}}/>
                                            <LockOutlinedIcon
                                                aria-hidden="true"
                                                sx={{
                                                    position: "absolute",
                                                    right: -6,
                                                    bottom: -4,
                                                    fontSize: 12,
                                                    bgcolor: "background.paper",
                                                    color: "text.secondary",
                                                    borderRadius: "50%",
                                                }}
                                            />
                                        </Box>
                                    )}
                                />
                            );
                        })}
                    </Stack>

                    <Stack
                        gap={0.75}
                        sx={{
                            mt: "auto",
                            pl: `${SIDEBAR_CONTENT_X_PADDING}px`,
                            pr: expanded ? `${SIDEBAR_CONTENT_X_PADDING}px` : 0,
                            pt: 1.5,
                            pb: hasScrollableContent ? 1.5 : 0,
                        }}
                    >
                        {FOOTER_LINKS.map(item => {
                            const active = isActivePath(pathname, item.link);

                            return (
                                <SidebarNavButton
                                    key={item.id}
                                    active={active}
                                    expanded={expanded}
                                    external={item.external}
                                    Icon={item.Icon}
                                    id={`dashboard-sidebar-footer-${item.id}`}
                                    label={t(item.label)}
                                    link={item.link}
                                />
                            );
                        })}
                    </Stack>
                </Stack>
            </Box>

            <Box
                sx={{
                    flexShrink: 0,
                    px: `${SIDEBAR_CONTENT_X_PADDING}px`,
                    pt: hasScrollableContent ? 1.5 : 0.75,
                    pb: 1.5,
                    borderTop: hasScrollableContent ? "1px solid" : 0,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                }}
            >
                <UserMenu expanded={expanded} onOpenChange={onUserMenuOpenChange}/>
            </Box>
        </Box>
    );
};

export default SidebarContent;
